<script setup lang="ts">
import type { SearchFilters } from "@gasparigit/omnia-sdk";
import type { Component } from "vue";
import { cn } from "~/lib/utils";

defineProps<{
  breadcrumbs: { href?: string; title?: string }[];
  backUrl?: string;
  title?: string;
  abstract?: string;
  search?: boolean;
  icon?: Component;
  skeleton?: boolean;
  searchFilters?: SearchFilters;
  searchStatistics?: boolean;
}>();
</script>

<template>
  <div :class="cn('mb-10 print:mb-0 bg-gradient-to-br from-primary-50 to-purple-50', $attrs.class)">
    <div class="py-8 print:p-0 print:pt-4 print:bg-transparent">
      <div class="main-content">
        <div class="md:flex gap-5">
          <span class="text-sm font-semibold hidden md:block print:hidden flex-shrink-0">Ti trovi in:</span>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <template v-for="breadcrumb in breadcrumbs">
                <BreadcrumbSeparator />
                <BreadcrumbPage class="truncate max-w-64" v-if="!breadcrumb?.href">
                  <Skeleton class="h-6 w-24 bg-primary/25" v-if="skeleton || !breadcrumb?.title" />
                  <span v-else>{{ breadcrumb.title }}</span>
                </BreadcrumbPage>
                <BreadcrumbItem v-else>
                  <BreadcrumbLink class="truncate max-w-64" :href="breadcrumb?.href">
                    <Skeleton class="h-6 w-24 bg-primary/25" v-if="skeleton || !breadcrumb?.title" />
                    <span v-else>{{ breadcrumb.title }}</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </template>
            </BreadcrumbList>
          </Breadcrumb>
          <slot name="info"></slot>
        </div>

        <div class="flex items-center mt-8 gap-x-3">
          <NuxtLink v-if="backUrl" :href="backUrl" class="text-primary hover:text-primary-600 transition-colors">
            <Icon name="ion:arrow-back-circle-outline" class="w-12 h-12" />
          </NuxtLink>

          <template v-if="'icon' in $slots">
            <Skeleton v-if="skeleton" class="h-12 w-12 bg-primary/25" />
            <slot v-else name="icon"></slot>
          </template>

          <template v-if="icon">
            <Skeleton v-if="skeleton" class="h-12 w-12 bg-primary/25" />
            <component v-else :is="icon" class="w-12 h-12"></component>
          </template>

          <template v-if="'title' in $slots">
            <slot name="title"></slot>
          </template>
          <template v-else>
            <Skeleton v-if="skeleton" class="h-10 w-full max-w-52 bg-primary/25" />
            <h1 v-else class="text-4xl font-extrabold">{{ title }}</h1>
          </template>
        </div>

        <div
          class="mt-5 prose max-w-full font-semibold text-lg print:text-base print:italic print:font-normal prose-a:text-primary"
          v-if="abstract?.length"
          v-html="abstract?.replace(/\n/g, '<br>')"
        ></div>
      </div>
    </div>

    <div class="flex items-center justify-center py-7 border-t border-purple-200 print:hidden" v-if="search || 'search' in $slots">
      <div v-if="search" class="max-w-3xl w-full px-3">
        <FrontSearchModal :show-statistics="searchStatistics" :initial-filters="searchFilters"></FrontSearchModal>
      </div>
      <slot v-else name="search"></slot>
    </div>
  </div>
</template>
